import { Checkbox, CheckboxButton } from '@e2m-customer-portal/ui';
import { FormControlLabel, Typography } from '@mui/material';
import { addHours, set } from 'date-fns';
import { range } from 'ramda';
import { Fragment, memo, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui-compat';

const useStyles = makeStyles()((theme) => {
  const checkboxItemWidth = 28;
  return {
    item: {
      flexGrow: 0,
      margin: 0,
      boxSizing: 'border-box',
    },
    fullWidth: {
      maxWidth: '100%',
      flexBasis: '100%',
    },
    error: {
      color: theme.palette.error.main,
    },
    checkboxControl: {
      marginLeft: 0,
    },
    checkboxLabel: {
      fontWeight: 600,
    },
    allCheckbox: {
      marginLeft: '-6px',
    },
    checkboxItem: {
      position: 'relative',
      maxWidth: `${checkboxItemWidth}px`,
      flexBasis: `${checkboxItemWidth}px`,
    },
    checkbox: {
      position: 'absolute',
      marginLeft: '-6px',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    buttonItem: {
      maxWidth: `calc(25% - ${checkboxItemWidth / 4}px)`,
      flexBasis: `calc(25% - ${checkboxItemWidth / 4}px)`,
      padding: '1px 2px 2px 1px',
    },
  };
});

export type HourSelection = Record<number, boolean>;

export const makeHourSelection = (selected: boolean, hourStep: number = 1): HourSelection =>
  range(0, 24 / hourStep).reduce(
    (selection, hour) => ({
      ...selection,
      [hour * hourStep]: selected,
    }),
    {},
  ) as HourSelection;

export interface TimeSliceSelectorProps {
  heading?: string;
  hourStep?: number;
  selected?: HourSelection;
  onChange?: (selected: HourSelection) => void;
  disabled?: HourSelection | boolean;
  error?: boolean;
  helperText?: string;
}

export function TimeSliceSelectorBase({
  heading,
  hourStep = 1,
  selected,
  onChange,
  disabled,
  error,
  helperText,
}: Readonly<TimeSliceSelectorProps>): ReactElement<TimeSliceSelectorProps> {
  const { classes, cx } = useStyles();
  const [t] = useTranslation();
  const selection = selected ?? makeHourSelection(false, hourStep);
  const disabledHours = typeof disabled === 'object' ? disabled : makeHourSelection(!!disabled, hourStep);

  const allChecked = range(0, 24 / hourStep).every((hour) => selection[hour * hourStep]);
  const someChecked = range(0, 24 / hourStep).some((hour) => selection[hour * hourStep]);
  const someDisabled = range(0, 24 / hourStep).some((hour) => disabledHours[hour * hourStep]);
  const handleAllChange = onChange && (() => onChange(makeHourSelection(!allChecked, hourStep)));

  // The next block is only relevant if hourStep === 1, so no special handling required
  const isLineChecked = (hour: number): boolean => range(hour, hour + 4).every((h) => selection[h]);
  const isLineSomeChecked = (hour: number): boolean => range(hour, hour + 4).some((h) => selection[h]);
  const isLineDisabled = (hour: number): boolean => range(hour, hour + 4).some((h) => disabledHours[h]);
  const handleLineChange = (hour: number): undefined | (() => void) =>
    onChange &&
    (() => {
      const check = !isLineChecked(hour);
      onChange({
        ...selection,
        ...range(hour, hour + 4).reduce((res, h) => ({ ...res, [h]: check }), {}),
      });
    });

  const handleChange = (hour: number): undefined | ((checked: boolean) => void) =>
    onChange && ((checked) => onChange({ ...selection, [hour]: checked }));

  return (
    <div className="box-border">
      {heading && (
        <div className={cx(classes.item, classes.fullWidth, { [classes.error]: error })}>
          <Typography variant="body1">{heading}</Typography>
        </div>
      )}

      {helperText && (
        <div className={cx(classes.item, classes.fullWidth, { [classes.error]: error })}>
          <Typography variant="body1">{helperText}</Typography>
        </div>
      )}

      <div className={cx(classes.item, classes.fullWidth)}>
        <FormControlLabel
          className={classes.checkboxControl}
          classes={{ label: classes.checkboxLabel }}
          control={
            <Checkbox
              className="-ml-1.5"
              checked={allChecked}
              indeterminate={someChecked && !allChecked}
              disabled={someDisabled}
              onChange={handleAllChange}
            />
          }
          label={t('TIME_SLICE_SELECTOR_SELECT_ALL')}
        />
      </div>

      <div className={hourStep === 4 ? 'grid grid-cols-3' : 'flex flex-wrap'}>
        {range(0, 24 / hourStep).map((hour) => {
          const date = set(new Date(), {
            hours: hour * hourStep,
            minutes: 0,
            seconds: 0,
            milliseconds: 0,
          });
          return (
            <Fragment key={hour}>
              {hourStep === 1 && hour % 4 === 0 && (
                <div className="relative m-0 box-border max-w-7 grow-0 basis-7 ">
                  <Checkbox
                    className="absolute top-1/2 -ml-1.5 -translate-y-1/2"
                    checked={isLineChecked(hour)}
                    indeterminate={!isLineChecked(hour) && isLineSomeChecked(hour)}
                    disabled={isLineDisabled(hour)}
                    onChange={handleLineChange(hour)}
                    ariaLabel={t('TIME_SLICE_SELECTOR_SLICE', { start: date, end: addHours(date, 4) })}
                  />
                </div>
              )}
              <div className={cx(classes.item, classes.buttonItem, hourStep === 4 && classes.fullWidth)}>
                <CheckboxButton
                  className="py-1.5 px-2 text-xs"
                  checked={selection[hour * hourStep]}
                  disabled={disabledHours[hour * hourStep]}
                  onChange={handleChange(hour * hourStep)}
                >
                  {t('TIME_SLICE_SELECTOR_SLICE', { start: date, end: addHours(date, hourStep) })}
                </CheckboxButton>
              </div>
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export const TimeSliceSelector = memo(TimeSliceSelectorBase);
